<template>
  <div class="scf-panel">
    <div class="scf-title">上传尺码图</div>

    <div
      class="scf-upload-zone"
      :class="{ 'has-image': sc.preview }"
      @click="triggerUpload"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <input ref="fileInput" type="file" accept="image/*" hidden @change="onFileChange" />
      <template v-if="sc.preview">
        <img :src="sc.preview" class="scf-preview" />
        <div class="scf-overlay">
          <span>点击重新上传</span>
        </div>
      </template>
      <template v-else>
        <svg viewBox="0 0 24 24" fill="none" stroke="#c9cdd4" stroke-width="1.5" width="40" height="40">
          <rect x="3" y="3" width="18" height="18" rx="2"/>
          <circle cx="8.5" cy="8.5" r="1.5"/>
          <polyline points="21 15 16 10 5 21"/>
        </svg>
        <span class="scf-hint">点击或拖拽上传中文尺码图</span>
      </template>
    </div>

    <button
      class="scf-extract-btn"
      :disabled="!sc.image || sc.loading"
      @click="workbench.extractSizeData(pi)"
    >
      {{ sc.loading && !sc.tableData ? '提取中...' : '提取数据与中英转换' }}
    </button>

    <!-- 底图选择 -->
    <div class="scf-section-title">选择底图（可选）</div>

    <div v-if="sc.bgFromStore" class="scf-selected-bg" @click="showPicker = true">
      <img :src="sc.bgFromStore" class="scf-selected-bg-img" />
      <div class="scf-overlay">
        <span>点击更换底图</span>
      </div>
    </div>
    <div v-else class="scf-picker-trigger" @click="showPicker = true">
      <svg viewBox="0 0 24 24" fill="none" stroke="#c9cdd4" stroke-width="1.5" width="32" height="32">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <circle cx="8.5" cy="8.5" r="1.5"/>
        <polyline points="21 15 16 10 5 21"/>
      </svg>
      <span class="scf-picker-hint">点击选择底图</span>
    </div>

    <!-- 弹窗选择 -->
    <div v-if="showPicker" class="scf-picker-overlay" @click.self="showPicker = false">
      <div class="scf-picker-modal">
        <div class="scf-picker-header">
          <span>选择底图</span>
          <button class="scf-picker-close" @click="showPicker = false">&times;</button>
        </div>
        <div v-if="availableImages.length" class="scf-image-grid">
          <div
            v-for="(img, idx) in availableImages"
            :key="idx"
            class="scf-grid-item"
            :class="{ selected: sc.bgFromStore === img.url }"
            @click="pickImage(img.url)"
          >
            <img :src="img.url" />
            <div class="scf-grid-label">{{ img.label }}</div>
            <div v-if="sc.bgFromStore === img.url" class="scf-grid-check">
              <svg viewBox="0 0 24 24" fill="#fff" width="12" height="12">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
            </div>
          </div>
        </div>
        <div v-else class="scf-no-images">暂无可选图片，请先生成宣传图或裂变图</div>
      </div>
    </div>

    <div class="scf-info-box">
      <div class="scf-info-title">自动处理规则</div>
      <ul>
        <li>cm → inch (1cm = 0.3937in)</li>
        <li>斤 → lb (1斤 = 1.1023lb)</li>
        <li>中文表头 → 英文电商术语</li>
        <li>合成时尺码图以 50% 透明度叠加在底图上</li>
      </ul>
    </div>

    <div v-if="sc.error" class="scf-error">{{ sc.error }}</div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed } from 'vue'
import { useWorkbenchStore } from '@/stores/useWorkbenchStore'

const props = defineProps({ productIndex: { type: Number, required: true } })
const workbench = useWorkbenchStore()
const pi = computed(() => props.productIndex)
const sc = computed(() => workbench.getSc(pi.value))

const fileInput = ref(null)
const showPicker = ref(false)
const triggerUpload = () => fileInput.value?.click()

const pickImage = (url) => {
  workbench.selectSizeChartBg(pi.value, url)
  showPicker.value = false
}

const onFileChange = (e) => {
  const file = e.target.files?.[0]
  if (file) workbench.setSizeChartFile(pi.value, file)
}

const onDrop = (e) => {
  const file = e.dataTransfer?.files?.[0]
  if (file) workbench.setSizeChartFile(pi.value, file)
}

const availableImages = computed(() => {
  const idx = pi.value
  const images = []

  const promoResults = workbench.imageGen.genResults[idx] || []
  promoResults.forEach((slot, si) => {
    if (slot.url) images.push({ url: slot.url, label: `宣传图 ${si + 1}` })
  })

  const fissionColors = workbench.imageGen.fissionColors[idx] || []
  fissionColors.forEach((color) => {
    (color.results || []).forEach((r, ri) => {
      if (r.url) images.push({ url: r.url, label: `${color.label} ${ri + 1}` })
    })
  })

  const mainResults = workbench.mainImageGen.results || {}
  for (const [key, val] of Object.entries(mainResults)) {
    if (!val.url) continue
    const isThisProduct =
      key.startsWith(`promo-${idx}-`) ||
      key.match(new RegExp(`^${idx}-\\d+$`))
    if (isThisProduct) {
      const label = key.startsWith('promo-') ? '主图(宣传)' : '主图(裂变)'
      images.push({ url: val.url, label })
    }
  }

  return images
})
</script>

<style scoped>
.scf-panel {
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.scf-title { font-size: 16px; font-weight: 600; color: #1d2129; }
.scf-section-title { font-size: 14px; font-weight: 500; color: #4e5969; margin-top: 4px; }

.scf-upload-zone {
  border: 2px dashed #e5e6eb; border-radius: 8px; min-height: 200px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; cursor: pointer; transition: border-color 0.2s; overflow: hidden; position: relative;
}
.scf-upload-zone:hover { border-color: #165dff; }
.scf-upload-zone.has-image { border-style: solid; border-color: #165dff; }
.scf-preview { width: 100%; height: 200px; object-fit: contain; }
.scf-overlay {
  position: absolute; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 14px; opacity: 0; transition: opacity 0.2s;
}
.scf-upload-zone:hover .scf-overlay { opacity: 1; }
.scf-hint { font-size: 13px; color: #86909c; }

.scf-extract-btn {
  padding: 10px 20px; background: #165dff; color: #fff; border: none;
  border-radius: 6px; font-size: 14px; cursor: pointer; transition: background 0.2s;
}
.scf-extract-btn:disabled { background: #c9cdd4; cursor: not-allowed; }
.scf-extract-btn:not(:disabled):hover { background: #0e4ddb; }

.scf-selected-bg {
  position: relative; border-radius: 8px; overflow: hidden; border: 2px solid #165dff;
  cursor: pointer;
}
.scf-selected-bg-img { width: 100%; max-height: 150px; object-fit: contain; display: block; background: #f7f8fa; }
.scf-selected-bg .scf-overlay {
  position: absolute; inset: 0; background: rgba(0,0,0,0.4);
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 13px; opacity: 0; transition: opacity 0.2s;
}
.scf-selected-bg:hover .scf-overlay { opacity: 1; }

.scf-picker-trigger {
  border: 2px dashed #e5e6eb; border-radius: 8px; min-height: 100px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; cursor: pointer; transition: border-color 0.2s;
}
.scf-picker-trigger:hover { border-color: #165dff; }
.scf-picker-hint { font-size: 13px; color: #86909c; }

.scf-picker-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000;
  display: flex; align-items: center; justify-content: center;
}
.scf-picker-modal {
  background: #fff; border-radius: 12px; width: 520px; max-width: 90vw;
  max-height: 80vh; display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}
.scf-picker-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 20px; border-bottom: 1px solid #e5e6eb; font-size: 15px; font-weight: 600;
}
.scf-picker-close {
  background: none; border: none; font-size: 22px; cursor: pointer;
  color: #86909c; line-height: 1; padding: 0 4px;
}
.scf-picker-close:hover { color: #1d2129; }

.scf-image-grid {
  display: grid; grid-template-columns: repeat(4, 1fr);
  gap: 8px; padding: 16px 20px; overflow-y: auto;
}
.scf-grid-item {
  position: relative; aspect-ratio: 1/1; border-radius: 6px; overflow: hidden;
  border: 2px solid transparent; cursor: pointer; transition: border-color 0.15s;
}
.scf-grid-item:hover { border-color: #c0d5ff; }
.scf-grid-item.selected { border-color: #165dff; }
.scf-grid-item img { width: 100%; height: 100%; object-fit: cover; }
.scf-grid-label {
  position: absolute; bottom: 0; left: 0; right: 0;
  background: rgba(0,0,0,0.5); color: #fff; font-size: 10px;
  text-align: center; padding: 2px 0; line-height: 1.3;
}
.scf-grid-check {
  position: absolute; top: 3px; right: 3px;
  width: 16px; height: 16px; background: #165dff; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
}
.scf-no-images { color: #c9cdd4; font-size: 12px; text-align: center; padding: 32px 0; }

.scf-info-box {
  background: #f7f8fa; border-radius: 6px; padding: 12px 16px;
  font-size: 12px; color: #4e5969;
}
.scf-info-title { font-weight: 600; margin-bottom: 6px; }
.scf-info-box ul { margin: 0; padding-left: 16px; }
.scf-info-box li { margin-bottom: 2px; }
.scf-error {
  color: #f53f3f; font-size: 13px; padding: 8px 12px;
  background: #fff0f0; border-radius: 4px;
}
</style>
